import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, Language, TranslationKey } from '@/i18n/translations';
import { getLanguageFromPath } from '@/lib/route-mapping';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  
  const [language, setLanguage] = useState<Language>(() => {
    // Initialize from URL path first, then localStorage
    const urlLang = getLanguageFromPath(window.location.pathname);
    if (urlLang) return urlLang;
    
    const stored = localStorage.getItem('language');
    return (stored === 'en' || stored === 'es' || stored === 'vi') ? stored : 'en';
  });

  // Update language when URL changes
  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang && urlLang !== language) {
      setLanguage(urlLang);
      localStorage.setItem('language', urlLang);
      document.documentElement.setAttribute('lang', urlLang);
    }
  }, [location.pathname]);

  const t = (key: TranslationKey): string => {
    return translations[language][key] || key;
  };

  const handleSetLanguage = (lang: Language) => {
    console.log('Changing language to:', lang);
    setLanguage(lang);
    localStorage.setItem('language', lang);
    // Force re-render by updating document attribute
    document.documentElement.setAttribute('lang', lang);
  };

  // Set initial lang attribute
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
